import type { Canvas, Object as FabricObject } from 'fabric/fabric-impl';
import { useEffect, useRef, useState } from 'react';
import { round } from 'shared/utils/round';
import type { Position, Size } from './transform-section';

/**
 * Fabric шлёт `object:moving` / `object:scaling` / `object:rotating` на каждый кадр
 * перетаскивания — уже после того, как SnapToGrid подвинул объект по сетке (он
 * подписан раньше инспектора). Поэтому инспектор, слушающий их, показывает те же
 * координаты, что и холст, прямо во время перетаскивания. `object:modified` и
 * `mouse:up` оставлены как завершающая синхронизация: они закрывают случаи, когда
 * объект двигали не мышью (стрелки, выравнивание, undo/redo).
 */
const LIVE_TRANSFORM_EVENTS = ['object:moving', 'object:scaling', 'object:rotating', 'object:modified', 'mouse:up'] as const;

/** Подписывает `handler` на все события, меняющие трансформацию объекта. Возвращает отписку. */
export function subscribeLiveTransform(canvas: Canvas | undefined, handler: () => void): () => void {
  if (!canvas) return () => undefined;
  for (const event of LIVE_TRANSFORM_EVENTS) canvas.on(event, handler);
  return () => {
    for (const event of LIVE_TRANSFORM_EVENTS) canvas.off(event, handler);
  };
}

export const readPositionByOrigin = (object: FabricObject): Position => {
  const { x, y } = object.getPointByOrigin('left', 'top');
  return { x: round(x), y: round(y) };
};

export const readScaledSize = (object: FabricObject): Size => ({
  width: round(object.getScaledWidth()),
  height: round(object.getScaledHeight()),
});

export interface LiveTransformReaders {
  readPosition?: (object: FabricObject) => Position;
  readSize?: (object: FabricObject) => Size;
}

export interface LiveTransform {
  position: Position;
  size: Size;
  setPosition: (position: Position) => void;
  setSize: (size: Size) => void;
}

/**
 * Держит расположение и размер объекта в правом тулбаре синхронными с холстом
 * в реальном времени. Сеттеры отданы наружу, чтобы инспектор мог показать
 * введённое значение сразу, не дожидаясь события от Fabric.
 */
export function useLiveTransform(
  object: FabricObject | undefined,
  canvas: Canvas | undefined,
  readers: LiveTransformReaders = {},
): LiveTransform {
  const { readPosition = readPositionByOrigin, readSize = readScaledSize } = readers;

  // Читалки приходят инлайн-стрелками, их идентичность меняется каждый рендер —
  // держим их в ref, иначе подписка пересоздавалась бы на каждый рендер.
  const readersRef = useRef({ readPosition, readSize });
  readersRef.current = { readPosition, readSize };

  const [position, setPosition] = useState<Position>(() => (object ? readPosition(object) : { x: 0, y: 0 }));
  const [size, setSize] = useState<Size>(() => (object ? readSize(object) : { width: 0, height: 0 }));

  useEffect(() => {
    if (!object) return;

    const sync = () => {
      const nextPosition = readersRef.current.readPosition(object);
      const nextSize = readersRef.current.readSize(object);
      setPosition((prev) => (prev.x === nextPosition.x && prev.y === nextPosition.y ? prev : nextPosition));
      setSize((prev) => (prev.width === nextSize.width && prev.height === nextSize.height ? prev : nextSize));
    };

    sync();
    return subscribeLiveTransform(canvas, sync);
  }, [canvas, object]);

  return { position, size, setPosition, setSize };
}
